import { useState, type FormEvent } from "react";
import { useChat } from "../hooks/useChat";
import { AddMemberDialog } from "./AddMemberDialog";
import { MessageSquare } from "lucide-react";
import styles from "./NewChatDialog.module.css";

interface NewChatDialogProps {
  onClose: () => void;
  onCreated: (channelId: string) => void;
}

export function NewChatDialog({ onClose, onCreated }: NewChatDialogProps) {
  const { createChannel } = useChat();
  const [mode, setMode] = useState<"choose" | "group" | "dm">("choose");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCreateGroup = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setError("");
    setLoading(true);

    try {
      const channel = await createChannel("group", name.trim(), []);
      onCreated(channel.id);
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Erstellen");
    } finally {
      setLoading(false);
    }
  };

  if (mode === "dm") {
    return (
      <AddMemberDialog
        title="Direktnachricht"
        description="Person für DM auswählen"
        onClose={onClose}
        onSelect={async (user) => {
          try {
            const channel = await createChannel("dm", "", [user.id]);
            onCreated(channel.id);
            onClose();
          } catch (err) {
            // DM might already exist
            setError((err as { message?: string }).message ?? "Fehler");
            setMode("choose");
          }
        }}
      />
    );
  }

  if (mode === "group") {
    return (
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
          <h3 className={styles.title}>Neue Gruppe</h3>

          <form onSubmit={handleCreateGroup}>
            <div className={styles.field}>
              <label className={styles.label}>Gruppenname</label>
              <input
                type="text"
                className={styles.input}
                placeholder="z.B. Projekt Alpha"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                required
              />
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.actions}>
              <button type="button" className={styles.cancelBtn} onClick={() => setMode("choose")}>
                Zurück
              </button>
              <button type="submit" className={styles.createBtn} disabled={loading || !name.trim()}>
                {loading ? "Erstelle..." : "Erstellen"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Choose mode
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.title}>Neuer Chat</h3>

        <div className={styles.modeList}>
          <button className={styles.modeBtn} onClick={() => setMode("dm")}>
            <span className={styles.modeIcon}><MessageSquare size={18} /></span>
            <div className={styles.modeInfo}>
              <span className={styles.modeName}>Direktnachricht</span>
              <span className={styles.modeDesc}>Private Nachricht an eine Person</span>
            </div>
          </button>

          <button className={styles.modeBtn} onClick={() => setMode("group")}>
            <span className={styles.modeIcon}>👥</span>
            <div className={styles.modeInfo}>
              <span className={styles.modeName}>Gruppe erstellen</span>
              <span className={styles.modeDesc}>Chat mit mehreren Personen</span>
            </div>
          </button>
        </div>

        <div className={styles.actions}>
          <button className={styles.cancelBtn} onClick={onClose}>
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}
